import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { invoiceMatchResultSchema, invoiceMatchStatusSchema, matchLineStatusSchema } from "./enums.js";
/**
 * Üçlü eşleştirme — sipariş ↔ mal kabul ↔ alış faturası (Prompt 16 aşama B, ADR 0016 §9–10). Eşleşme market
 * modülündedir ve FATURA TABLOSUNA YAZMAZ. Karşılaştırma ürün bazındadır: fatura birim fiyatı NET'tir (satır
 * `net_amount` / miktar — iskonto sonrası, KDV hariç). Toleranslar market ayarından gelir ve eşleşmede DONDURULUR.
 *
 * Tam eşleşme kesin maliyeti HEMEN uygular; farklı eşleşme onay motoruna (`INVOICE_MATCH_DIFFERENCE`) gider, onayda
 * uygulanır. Kesin maliyet = `COST_ADJUSTMENT` hareketi (miktar 0) + satılmış payı için bir sonraki SMM kapanışına
 * giren maliyet düzeltmesi.
 */
export const matchTolerancesSchema = z.object({
    quantityPercent: z.string(),
    pricePercent: z.string(),
    amountAbsolute: moneySchema,
});
export const invoiceMatchLineSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema.nullable(),
    productCode: z.string().nullable(),
    productName: z.string().nullable(),
    description: z.string().nullable().describe("Ürünsüz fatura satırının açıklaması"),
    status: matchLineStatusSchema,
    orderedQuantity: quantitySchema.nullable(),
    orderUnitPrice: unitPriceSchema.nullable(),
    receivedQuantity: quantitySchema,
    receiptUnitCost: unitPriceSchema.nullable(),
    invoicedQuantity: quantitySchema,
    invoiceUnitPrice: z
        .string()
        .nullable()
        .describe("Net (iskonto sonrası) KDV hariç birim fiyat (₺, 6 ondalık)"),
    quantityDifference: quantitySchema.describe("Faturalanan − kabul edilen"),
    unitPriceDifference: unitPriceSchema.nullable().describe("Fatura − referans (sipariş, yoksa kabul) fiyatı"),
    amountDifference: moneySchema.describe("Faturanın fazla istediği tutar (kuruş)"),
});
export const invoiceMatchReceiptSchema = z.object({
    receiptId: uuidSchema,
    receiptNo: z.string(),
    despatchNo: z.string().nullable(),
    stationId: uuidSchema,
    stationName: z.string(),
    netAmount: moneySchema,
    isActive: z.boolean(),
});
export const invoiceMatchSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    invoiceId: uuidSchema,
    invoiceNo: z.string().nullable(),
    invoiceIssueDate: localDateSchema,
    invoiceNetAmount: moneySchema,
    supplierId: uuidSchema,
    supplierName: z.string(),
    status: invoiceMatchStatusSchema,
    result: invoiceMatchResultSchema,
    tolerances: matchTolerancesSchema,
    amountDifference: moneySchema.describe("Σ satır tutar farkı (kuruş; + = faturanın fazlası)"),
    costAdjustment: moneySchema.describe("Uygulanan kesin maliyet farkı (kuruş; stok + SMM payı)"),
    automatic: z.boolean().describe("true → fatura olayıyla kendiliğinden eşleşti (kullanıcı yok)"),
    matchedBy: uuidSchema.nullable(),
    matchedAt: timestampSchema,
    approvedBy: uuidSchema.nullable(),
    approvedAt: timestampSchema.nullable(),
    approvalRequestId: uuidSchema.nullable(),
    unmatchedReason: z.string().nullable(),
    differenceLineCount: z.number().int(),
    receipts: z.array(invoiceMatchReceiptSchema),
    lines: z.array(invoiceMatchLineSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    version: z.number().int(),
});
export const invoiceMatchPageSchema = pageOf(invoiceMatchSchema);
export const listInvoiceMatchesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    supplierId: uuidSchema.optional(),
    status: invoiceMatchStatusSchema.optional(),
    invoiceId: uuidSchema.optional(),
});
export const invoiceMatchCandidatesQuerySchema = z.object({
    companyId: uuidSchema,
    supplierId: uuidSchema,
    stationId: uuidSchema.optional(),
});
export const invoiceMatchCandidatesSchema = z.object({
    invoices: z.array(z.object({
        invoiceId: uuidSchema,
        invoiceNo: z.string().nullable(),
        issueDate: localDateSchema,
        stationId: uuidSchema.nullable(),
        netAmount: moneySchema,
        payableAmount: moneySchema,
        lineCount: z.number().int(),
        despatchNos: z.array(z.string()).describe("Faturaya bağlı irsaliye numaraları"),
    })),
    receipts: z.array(z.object({
        receiptId: uuidSchema,
        receiptNo: z.string(),
        stationId: uuidSchema,
        stationName: z.string(),
        despatchNo: z.string().nullable(),
        despatchDate: localDateSchema.nullable(),
        businessDate: localDateSchema,
        purchaseOrderNo: z.string().nullable(),
        netAmount: moneySchema,
        lineCount: z.number().int(),
    })),
});
export const createInvoiceMatchSchema = z.object({
    invoiceId: uuidSchema,
    receiptIds: z
        .array(uuidSchema)
        .min(1, "En az bir mal kabul seçin")
        .max(50)
        .refine((ids) => new Set(ids).size === ids.length, "Aynı mal kabul iki kez seçilemez"),
});
export const unmatchInvoiceSchema = z.object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(3, "Gerekçe yazın").max(500),
});
//# sourceMappingURL=invoice-matches.js.map